/**
 * RhNav — bandeau de navigation Ressources humaines regroupé (juillet 2026).
 *
 * Remplace les 13 onglets à plat (barre à rallonge, fouillis) par 5 entrées, sur
 * le même principe que ComptaNav / GrcNav pour rester cohérent partout :
 *
 *   À valider · Plannings & temps ▾ · Paie ▾ · Documents ▾ · Mes demandes · ⚙
 *
 *   Plannings & temps ▾ : Plannings · Congés · Heures sup · Absences · Profils horaires
 *   Paie ▾              : Consolidation · Paie & clôture · Export & API
 *   Documents ▾         : Documents · Notes de frais
 *   À valider           : entrée simple (accueil gestionnaire)
 *   Mes demandes        : entrée simple (vue perso)
 *   Paramètres          : bouton icône seul, à droite (⚙)
 *
 * Comme ComptaNav :
 *  - menu rendu en position: fixed (échappe overflow / backdrop-blur / sticky) ;
 *  - ferme au clic extérieur, au scroll et au resize ;
 *  - le bouton de groupe reste NEUTRE (pas de surlignage) — seul le contenu change ;
 *  - l'action est un onglet interne (setTab), pas une navigation de route :
 *    RH est une page unique à onglets.
 *
 * Pour l'agent (accès restreint), seules « Mes demandes » et le groupe
 * « Documents » (Documents · Notes de frais) sont visibles — cohérent avec le
 * cloisonnement des autres briques.
 */
import { useState, useRef, useEffect } from 'react';
import { BadgeCheck, ChevronDown, Users, UserRound, Layers, Network, GraduationCap, Stamp } from 'lucide-react';
import {
  ClipboardCheck, CalendarClock, CalendarDays, Plane, Clock, CalendarX,
  SlidersHorizontal, Coins, FileCheck2, Upload, FileText, Receipt,
  Settings,
} from 'lucide-react';

export type RhTab =
  | 'mesdemandes' | 'monplanning' | 'plannings' | 'avalider' | 'cloture' | 'paie' | 'export'
  | 'conges' | 'heuressup' | 'absences' | 'profils' | 'documents' | 'notesfrais'
  | 'habilitations' | 'dossiersagents' | 'mondossier' | 'referentiels' | 'organigramme'
  | 'formations' | 'arretes' | 'parametres';

type Item = { k: RhTab; label: string; icon: React.ComponentType<{ className?: string }> };

// Onglets accessibles à un agent (accès restreint).
// « Mon planning » est ouvert à TOUS : c'est le point d'entrée universel du
// planning individuel (décision 23/07), y compris pour les agents du service
// RH qui n'ont aucune brique de planning.
// « Mon dossier » (28/07) : la lecture que l'agent fait de SES données. Ouvert
// à tous, gestionnaires compris — un chef de service a lui aussi un dossier.
const AGENT_TABS: RhTab[] = ['mesdemandes', 'monplanning', 'documents', 'notesfrais', 'mondossier'];

const PLANNINGS_TEMPS: Item[] = [
  { k: 'plannings', label: 'Plannings', icon: CalendarDays },
  { k: 'conges', label: 'Congés', icon: Plane },
  { k: 'heuressup', label: 'Heures sup', icon: Clock },
  { k: 'absences', label: 'Absences', icon: CalendarX },
  { k: 'profils', label: 'Profils horaires', icon: SlidersHorizontal },
];
const PAIE: Item[] = [
  { k: 'paie', label: 'Consolidation', icon: Coins },
  { k: 'cloture', label: 'Paie & clôture', icon: FileCheck2 },
  { k: 'export', label: 'Export & API', icon: Upload },
];
/**
 * Groupe « Suivi » (ex-« Documents », renommé le 24/07).
 *
 * Le groupe ne contient plus seulement des documents : il rassemble tout ce
 * qu'on SUIT sur un agent — ses pièces, ses habilitations, son suivi médical,
 * et désormais son dossier. « Documents » ne décrivait plus le contenu.
 *
 * `dossiersagents` en tête : c'est la table socle dont les autres découlent.
 */
const DOCUMENTS: Item[] = [
  { k: 'dossiersagents', label: 'Dossiers agents', icon: Users },
  // En lecture seule, et déduit du compte connecté : distinct de « Dossiers
  // agents », qui est l'écran de gestion.
  { k: 'mondossier', label: 'Mon dossier', icon: UserRound },
  { k: 'documents', label: 'Documents', icon: FileText },
  { k: 'notesfrais', label: 'Notes de frais', icon: Receipt },
  // Une habilitation, c'est un document avec une date de péremption : sa place
  // est ici, à côté des autres pièces de l'agent.
  { k: 'habilitations', label: 'Habilitations & suivi médical', icon: BadgeCheck },
  // Les valeurs nommées une fois (grades, cadres, types d'habilitation) qui
  // alimentent les formulaires ET les listes déroulantes du fichier Excel.
  { k: 'referentiels', label: 'Référentiels', icon: Layers },
  // Mig 238 — organigramme (5.13) et langues parlées (5.24). Déduits des
  // dossiers agents : leur place est dans le groupe qui les porte.
  { k: 'organigramme', label: 'Organigramme & langues', icon: Network },
  // Mig 240 — le parcours de formation (5.20) et les arrêtés (5.22 / 5.23).
  // Leur place est dans « Suivi » : ce sont des choses qu'on suit sur un agent.
  { k: 'formations', label: 'Formations', icon: GraduationCap },
  { k: 'arretes', label: 'Arrêtés', icon: Stamp },
];

const ACCENT = 'oklch(var(--c-rh))';

/** Bouton d'onglet simple (À valider, Mes demandes, Paramètres). */
function Tab({
  active, onClick, icon: Icon, children, iconOnly, title,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ComponentType<{ className?: string }>;
  children?: React.ReactNode;
  iconOnly?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={iconOnly ? title : undefined}
      className={`inline-flex items-center gap-1.5 rounded-r-pill border px-3 py-1.5 text-xs font-medium transition ${
        active
          ? 'text-white'
          : 'border-border bg-transparent text-ink-muted hover:bg-surface/40 hover:text-ink'
      }`}
      style={active ? { background: ACCENT, borderColor: 'transparent' } : undefined}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {!iconOnly && children}
    </button>
  );
}

/** Groupe déroulant (Plannings & temps, Paie, Documents) — reste neutre. */
function GroupMenu({
  label, items, tab, setTab, estGestion,
}: {
  label: string;
  items: Item[];
  tab: RhTab;
  setTab: (t: RhTab) => void;
  estGestion: boolean;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const visibles = items.filter((it) => estGestion || AGENT_TABS.includes(it.k));
  if (visibles.length === 0) return null;

  function toggle() {
    setOpen((o) => {
      const next = !o;
      if (next && btnRef.current) {
        const r = btnRef.current.getBoundingClientRect();
        setPos({ top: r.bottom + 4, left: r.left });
      }
      return next;
    });
  }

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      const t = e.target as Node;
      if (
        menuRef.current && !menuRef.current.contains(t) &&
        btnRef.current && !btnRef.current.contains(t)
      ) setOpen(false);
    }
    function onScrollOrResize() { setOpen(false); }
    document.addEventListener('mousedown', onClickOutside);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 rounded-r-pill border border-border bg-transparent px-3 py-1.5 text-xs font-medium text-ink-muted transition hover:bg-surface/40 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
        style={{ ['--tw-ring-color' as string]: ACCENT }}
      >
        {label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && pos && (
        <div
          ref={menuRef}
          role="menu"
          style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999 }}
          className="min-w-[210px] rounded-r-md border border-border bg-surface shadow-r-lg p-1"
        >
          {visibles.map((it) => (
            <button
              key={it.k}
              role="menuitem"
              type="button"
              onClick={() => { setOpen(false); setTab(it.k); }}
              className={`flex w-full items-center gap-2 rounded-r-sm px-3 py-2 text-left text-xs transition hover:bg-surface/60 ${
                tab === it.k ? 'text-ink' : 'text-ink-muted hover:text-ink'
              }`}
              style={tab === it.k ? { color: ACCENT } : undefined}
            >
              <it.icon className="h-3.5 w-3.5" aria-hidden="true" />
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function RhNav({
  tab, setTab, estGestion,
}: {
  tab: RhTab;
  setTab: (t: RhTab) => void;
  estGestion: boolean;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 border-b border-border pb-3">
      {/* À valider : accueil gestionnaire uniquement */}
      {estGestion && (
        <Tab active={tab === 'avalider'} onClick={() => setTab('avalider')} icon={ClipboardCheck}>
          À valider
        </Tab>
      )}

      {estGestion && (
        <GroupMenu label="Plannings & temps" items={PLANNINGS_TEMPS} tab={tab} setTab={setTab} estGestion={estGestion} />
      )}
      {estGestion && (
        <GroupMenu label="Paie" items={PAIE} tab={tab} setTab={setTab} estGestion={estGestion} />
      )}

      {/* Suivi : visible aussi pour l'agent (Documents + Notes de frais). Les
          dossiers agents restent réservés à l'encadrement (garde côté écran). */}
      <GroupMenu label="Suivi" items={DOCUMENTS} tab={tab} setTab={setTab} estGestion={estGestion} />

      {/* Mon planning + Mes demandes : le duo perso, accessible à tous. Le
          planning individuel vit ici et non dans les briques (décision 23/07). */}
      <Tab active={tab === 'monplanning'} onClick={() => setTab('monplanning')} icon={CalendarDays}>
        Mon planning
      </Tab>

      <Tab active={tab === 'mesdemandes'} onClick={() => setTab('mesdemandes')} icon={CalendarClock}>
        Mes demandes
      </Tab>

      {/* Paramètres : icône seule, poussée à droite */}
      {estGestion && (
        <div className="ml-auto">
          <Tab
            active={tab === 'parametres'}
            onClick={() => setTab('parametres')}
            icon={Settings}
            iconOnly
            title="Paramètres"
          />
        </div>
      )}
    </div>
  );
}
